import { useState, useEffect } from 'react'
import { Table, Tabs, Input, Button } from 'antd'
import { SearchOutlined, EyeOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { participantApi, Participant, Team } from '../services/participant'
import { hackathonApi } from '../services/hackathon'
import Pagination from '../components/Pagination'
import ParticipantDetailsModal from '../components/ParticipantDetailsModal'
import TeamDetailsModal from '../components/TeamDetailsModal'
import { capitalizeGender } from '../utils/capitalizeGender'

const Participants = () => {
  const [activeTab, setActiveTab] = useState('participants')
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('')
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  })
  const [selectedParticipant, setSelectedParticipant] = useState<Participant | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null)
  const [isTeamModalOpen, setIsTeamModalOpen] = useState(false)
  const [teamPagination, setTeamPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  })

  // Fetch all hackathons to find the active one
  const { data: hackathonsData } = useQuery({
    queryKey: ['hackathons'],
    queryFn: hackathonApi.getHackathons,
    retry: 1,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })

  // Find the active hackathon
  const activeHackathon = hackathonsData?.data?.find((hackathon: any) => hackathon.isActive === true)
  const hackathonId = activeHackathon?._id

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery)
      // Reset pagination when searching
      if (searchQuery !== debouncedSearchQuery) {
        setPagination(prev => ({ ...prev, current: 1 }))
        setTeamPagination(prev => ({ ...prev, current: 1 }))
      }
    }, 500)

    return () => clearTimeout(timer)
  }, [searchQuery, debouncedSearchQuery])

  // Fetch participants data using React Query for active hackathon
  const { 
    data: participantsResponse, 
    isLoading: isLoadingParticipants
  } = useQuery({
    queryKey: ['participants-hackathon', hackathonId, pagination.current, pagination.pageSize, debouncedSearchQuery],
    queryFn: () => participantApi.getParticipantsByHackathon(hackathonId!, {
      page: pagination.current,
      limit: pagination.pageSize,
      search: debouncedSearchQuery || undefined,
    }),
    enabled: !!hackathonId, // Only fetch when we have an active hackathon ID
    retry: 1,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })

  // Fetch teams data using React Query for active hackathon
  const { 
    data: teamsResponse, 
    isLoading: isLoadingTeams
  } = useQuery({
    queryKey: ['teams-hackathon', hackathonId, teamPagination.current, teamPagination.pageSize, debouncedSearchQuery],
    queryFn: () => participantApi.getTeamsByHackathon(hackathonId!, {
      page: teamPagination.current,
      limit: teamPagination.pageSize,
      search: debouncedSearchQuery || undefined,
    }),
    enabled: !!hackathonId, // Only fetch when we have an active hackathon ID
    retry: 1,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })

  // Get the data to display - no filtering needed since API already returns hackathon-specific data
  const displayData = participantsResponse?.data || []
  const teamsData = teamsResponse?.data || []
  const isLoading = isLoadingParticipants || !hackathonsData // Also loading while fetching hackathons
  const isLoadingTeamsData = isLoadingTeams || !hackathonsData
  
  const currentPagination = participantsResponse?.pagination || {
    currentPage: pagination.current,
    totalPages: 0,
    totalItems: 0,
    itemsPerPage: pagination.pageSize,
    hasNextPage: false,
    hasPrevPage: false,
    nextPage: null,
    prevPage: null,
  }

  const teamsPagination = teamsResponse?.pagination || {
    currentPage: teamPagination.current,
    totalPages: 0,
    totalItems: 0,
    itemsPerPage: teamPagination.pageSize,
    hasNextPage: false,
    hasPrevPage: false,
    nextPage: null,
    prevPage: null,
  }

  // Handle pagination change
  const handlePageChange = (page: number) => {
    setPagination(prev => ({
      ...prev,
      current: page
    }))
  }

  // Handle team pagination change
  const handleTeamPageChange = (page: number) => {
    setTeamPagination(prev => ({
      ...prev,
      current: page
    }))
  }

  // Clear search when switching tabs
  const handleTabChange = (key: string) => {
    setActiveTab(key)
    if (key !== 'participants') {
      setSearchQuery('')
    }
  }

  // Handle view details
  const handleViewDetails = (participant: Participant) => {
    setSelectedParticipant(participant)
    setIsModalOpen(true)
  }

  // Handle modal close
  const handleModalClose = () => {
    setIsModalOpen(false)
    setSelectedParticipant(null)
  }

  // Handle team modal close
  const handleTeamModalClose = () => {
    setIsTeamModalOpen(false)
    setSelectedTeam(null)
  }

  // Participants table columns
  const participantsColumns = [
    {
      title: 'Name',
      dataIndex: 'userId',
      key: 'name',
      render: (userId: any) => (
        <div>
          <span className="font-medium text-gray-900">{userId?.fullName || 'N/A'}</span>
        </div>
      ),
    },
    {
      title: 'Email Address',
      dataIndex: 'userId',
      key: 'email',
      render: (userId: any) => (
        <span className="text-gray-600">{userId?.email || 'N/A'}</span>
      ),
    },
    {
      title: 'Phone number',
      dataIndex: 'userId',
      key: 'phone',
      render: (userId: any) => (
        <span className="text-gray-600">{userId?.phoneNumber || 'N/A'}</span>
      ),
    },
    {
      title: 'Gender',
      dataIndex: 'userId',
      key: 'role',
      render: (userId: any) => (
        <span className="text-gray-600">{capitalizeGender(userId?.gender)}</span>
      ),
    },
    {
      title: 'State of Residence',
      dataIndex: 'userId',
      key: 'organization',
      render: (userId: any) => (
        <span className="text-gray-600">{userId?.state || 'N/A'}</span>
      ),
    },
   
    
    {
      title: 'Action',
      key: 'action',
      render: (record: any) => (
        <Button 
          type="text" 
          icon={<EyeOutlined />}
          className="text-gray-500 hover:text-primary"
          onClick={() => handleViewDetails(record)}
        >
          View details
        </Button>
      ),
    },
  ]

  // Handle view team details
  const handleViewTeamDetails = (team: any) => {
    setSelectedTeam(team)
    setIsTeamModalOpen(true)
  }

  // Teams table columns
  const teamsColumns = [
    {
      title: 'Team Name',
      dataIndex: 'name',
      key: 'name',
      render: (name: string) => (
        <span className="font-medium text-gray-900">{name}</span>
      ),
    },
    {
      title: 'Team Leader',
      dataIndex: 'participants',
      key: 'leader',
      render: (participants: any[]) => {
        const leader = participants?.find((p) => p.leader)
        return (
          <span className="text-gray-600">
            {leader?.userId?.fullName || 'N/A'}
          </span>
        )
      },
    },
    {
      title: 'Team Member',
      dataIndex: 'participants',
      key: 'member',
      render: (participants: any[]) => {
        const member = participants?.find((p) => !p.leader)
        return (
          <span className="text-gray-600">
            {member?.userId?.fullName || 'N/A'}
          </span>
        )
      },
    },
    {
      title: 'Date Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (createdAt: string) => (
        <span className="text-gray-600">
          {new Date(createdAt).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          })}
        </span>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      render: (record: any) => (
        <Button 
          type="text" 
          icon={<EyeOutlined />}
          className="text-gray-500 hover:text-primary"
          onClick={() => handleViewTeamDetails(record)}
        >
          View details
        </Button>
      ),
    },
  ]

  const tabItems = [
    {
      key: 'participants',
      label: `All Participants (${currentPagination.totalItems || 0})`,
      children: (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {isLoading ? (
            <div className="overflow-hidden">
              {/* Table Header Skeleton */}
              <div className="bg-gray-50 border-b border-gray-200 px-6 py-3">
                <div className="flex space-x-6">
                  <div className="w-48 h-4 bg-gray-200 rounded animate-pulse"></div>
                  <div className="w-40 h-4 bg-gray-200 rounded animate-pulse"></div>
                  <div className="w-32 h-4 bg-gray-200 rounded animate-pulse"></div>
                  <div className="w-36 h-4 bg-gray-200 rounded animate-pulse"></div>
                  <div className="w-24 h-4 bg-gray-200 rounded animate-pulse"></div>
                  <div className="w-20 h-4 bg-gray-200 rounded animate-pulse"></div>
                </div>
              </div>
              
              {/* Table Rows Skeleton */}
              {[...Array(5)].map((_, index) => (
                <div key={index} className="border-b border-gray-200 px-6 py-4">
                  <div className="flex space-x-6 items-center">
                    <div className="w-48">
                      <div className="w-32 h-4 bg-gray-200 rounded animate-pulse mb-1"></div>
                      <div className="w-24 h-3 bg-gray-100 rounded animate-pulse"></div>
                    </div>
                    <div className="w-40 h-4 bg-gray-200 rounded animate-pulse"></div>
                    <div className="w-32 h-4 bg-gray-200 rounded animate-pulse"></div>
                    <div className="w-36 h-4 bg-gray-200 rounded animate-pulse"></div>
                    <div className="w-24 h-4 bg-gray-200 rounded animate-pulse"></div>
                    <div className="w-20 h-6 bg-gray-200 rounded-full animate-pulse"></div>
                    <div className="w-24 h-4 bg-gray-200 rounded animate-pulse"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : !hackathonId ? (
            <div className="text-center py-12">
              <div className="text-gray-400 text-6xl mb-4">🏆</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Active Hackathon</h3>
              <p className="text-gray-500">
                There is currently no active hackathon. Participants will appear here once a hackathon is active.
              </p>
            </div>
          ) : (
            <div>
              <Table 
                columns={participantsColumns} 
                dataSource={displayData?.map((participant: any) => ({
                  ...participant,
                  key: participant._id
                }))}
                pagination={false}
                className="participants-table"
                loading={isLoading}
              />
              
              {/* Custom Pagination */}
              {currentPagination && currentPagination.totalPages > 1 && (
                <div className="my-5   w-full">
                  <Pagination
                    currentPage={currentPagination.currentPage}
                    totalPages={currentPagination.totalPages}
                    onPageChange={handlePageChange}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'teams',
      label: `Teams (${teamsPagination.totalItems || 0})`,
      children: (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {isLoadingTeamsData ? (
            <div className="overflow-hidden">
              {/* Table Header Skeleton */}
              <div className="bg-gray-50 border-b border-gray-200 px-6 py-3">
                <div className="flex space-x-6">
                  <div className="w-48 h-4 bg-gray-200 rounded animate-pulse"></div>
                  <div className="w-40 h-4 bg-gray-200 rounded animate-pulse"></div>
                  <div className="w-32 h-4 bg-gray-200 rounded animate-pulse"></div>
                  <div className="w-36 h-4 bg-gray-200 rounded animate-pulse"></div>
                </div>
              </div>
              
              {/* Table Rows Skeleton */}
              {[...Array(5)].map((_, index) => (
                <div key={index} className="border-b border-gray-200 px-6 py-4">
                  <div className="flex space-x-6 items-center">
                    <div className="w-48 h-4 bg-gray-200 rounded animate-pulse"></div>
                    <div className="w-40 h-4 bg-gray-200 rounded animate-pulse"></div>
                    <div className="w-32 h-4 bg-gray-200 rounded animate-pulse"></div>
                    <div className="w-36 h-4 bg-gray-200 rounded animate-pulse"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : !hackathonId ? (
            <div className="text-center py-12">
              <div className="text-gray-400 text-6xl mb-4">🏆</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Active Hackathon</h3>
              <p className="text-gray-500">
                There is currently no active hackathon. Teams will appear here once a hackathon is active.
              </p>
            </div>
          ) : (
            <div>
              <Table 
                columns={teamsColumns} 
                dataSource={teamsData?.map((team: any) => ({
                  ...team,
                  key: team._id
                }))}
                pagination={false}
                className="participants-table"
                loading={isLoadingTeamsData}
              />
              
              {/* Custom Pagination */}
              {teamsPagination && teamsPagination.totalPages > 1 && (
                <div className="my-5 w-full">
                  <Pagination
                    currentPage={teamsPagination.currentPage}
                    totalPages={teamsPagination.totalPages}
                    onPageChange={handleTeamPageChange}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      ),
    },
  ]

  return (
    <div className="p-6 h-full flex flex-col">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-semibold text-gray-900">Participants</h1>
          <div className="flex items-center">
            <Input
              placeholder={
                activeTab === 'participants' 
                  ? "Search for participant name, email, or phone"
                  : "Search for team name or team member details"
              }
              prefix={<SearchOutlined className="text-gray-400" />}
              className="w-80 h-10 rounded-lg border-gray-300 focus:border-primary focus:ring-primary"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              allowClear
            />
          </div>
        </div>
        
        {/* Tabs */}
        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          items={tabItems}
          className="rubric-tabs flex-1 participants-tabs"
          tabBarGutter={32}
          tabBarStyle={{
            color: "#010ED0", // inactive tab text color
          }}
          moreIcon={null}
        />
      </div>

      {/* Participant Details Modal */}
      <ParticipantDetailsModal
        participant={selectedParticipant}
        isOpen={isModalOpen}
        onClose={handleModalClose}
      />

      {/* Team Details Modal */}
      <TeamDetailsModal
        team={selectedTeam as any}
        isOpen={isTeamModalOpen}
        onClose={handleTeamModalClose}
      />
    </div>
  )
}

export default Participants
